import React, { useState, useMemo, useEffect, useRef, memo } from 'react';
import { createPortal } from 'react-dom';
import Layout from '../components/layout/Layout';
import Button from '../components/ui/Button';
import { PageLoader } from '../components/ui/Spinner';
import { useApp } from '../context/AppContext';
import * as api from '../services/api';
import { fmtD } from '../utils/format';

// ── Image helpers ──────────────────────────────────────────────
const _urlCache = new Map();
const _inflightCache = new Map();
const _preloaded = new Set();

function preloadImg(url) {
  if (!url || _preloaded.has(url)) return;
  const img = new Image();
  img.src = url;
  _preloaded.add(url);
}

function resolveImageUrl(id) {
  if (_urlCache.has(id)) return Promise.resolve(_urlCache.get(id));
  if (_inflightCache.has(id)) return _inflightCache.get(id);

  const promise = api.getWorkcardImage(id)
    .then(res => {
      const url = res?.url || null;
      if (url) {
        _urlCache.set(id, url);
        preloadImg(url);
      }
      _inflightCache.delete(id);
      return url;
    })
    .catch(err => {
      console.error('Failed to load workcard image:', err);
      _inflightCache.delete(id);
      return null;
    });

  _inflightCache.set(id, promise);
  return promise;
}

// ── WorkcardImage Component ────────────────────────────────────
const WorkcardImage = memo(function WorkcardImage({ id, ticketNo }) {
  const cachedUrl = _urlCache.get(id) || null;

  const [imgUrl, setImgUrl] = useState(cachedUrl);
  const [loading, setLoading] = useState(!cachedUrl);
  const [zoomed, setZoomed] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    if (cachedUrl) return;
    resolveImageUrl(id).then(url => {
      if (mountedRef.current) {
        setImgUrl(url);
        setLoading(false);
      }
    });
    return () => { mountedRef.current = false; };
  }, [id, cachedUrl]);

  if (loading) {
    return <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Loading…</span>;
  }

  if (!imgUrl) {
    return <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>—</span>;
  }

  return (
    <>
      <div 
        onClick={(e) => { e.stopPropagation(); setZoomed(true); }}
        style={{
          width: 44,
          height: 44,
          borderRadius: 'var(--radius-sm)',
          border: '1px solid var(--border-color)',
          overflow: 'hidden',
          cursor: 'zoom-in',
          backgroundColor: '#0f172a',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'transform 0.2s',
        }}
        className="thumbnail-preview"
      >
        <img 
          src={imgUrl} 
          alt={`Ticket #${ticketNo} Preview`} 
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      </div>

      {zoomed && createPortal(
        <div 
          className="image-popup-overlay" 
          onClick={() => setZoomed(false)}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center',
            justifyContent: 'center', zIndex: 10000, cursor: 'zoom-out',
            animation: 'fadeIn 0.2s ease'
          }}
        >
          <div style={{ position: 'relative', maxWidth: '90%', maxHeight: '90%' }} onClick={e => e.stopPropagation()}>
            <button 
              onClick={() => setZoomed(false)}
              style={{
                position: 'absolute', top: -40, right: 0, background: 'none', border: 'none',
                color: 'white', fontSize: 24, cursor: 'pointer'
              }}
            >
              <i className="fa-solid fa-xmark"></i>
            </button>
            <img 
              src={imgUrl} 
              alt={`Ticket #${ticketNo} Scan`} 
              style={{ maxWidth: '100%', maxHeight: '80vh', borderRadius: 8, boxShadow: '0 8px 30px rgba(0,0,0,0.5)', cursor: 'default' }} 
            />
          </div>
        </div>,
        document.body
      )}
    </>
  );
});

// ── Manual Workcard Creation Modal ─────────────────────────────
function CreateWorkcardModal({ clients, onClose, onSubmit }) {
  const { doCreateAdhocClient } = useApp();
  const [clientId, setClientId]     = useState('');
  const [stockOptions, setStockOptions] = useState([]);
  const [stockLoading, setStockLoading] = useState(false);
  const [selectedStock, setSelectedStock] = useState(null);
  const [quantity, setQuantity]     = useState('');
  const [ticketNumber, setTicketNumber] = useState('');
  const [jobType, setJobType]       = useState('');
  const [colors, setColors]         = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState('');

  // Ad-hoc client state
  const [showAdhocForm, setShowAdhocForm] = useState(false);
  const [adhocForm, setAdhocForm] = useState({ name: '', gstin: '', phone: '', address: '' });
  const [skipStock, setSkipStock] = useState(false);
  
  // Custom stock state for skipped tracking
  const [customSize, setCustomSize] = useState('');
  const [customGsm, setCustomGsm]   = useState('');

  const selectedClientInfo = useMemo(() => clients.find(c => c.id === clientId), [clients, clientId]);
  const isAdhocSelected = selectedClientInfo?.is_adhoc === true;

  // Reset skipStock if we switch away from an adhoc client
  useEffect(() => {
    if (!isAdhocSelected) {
      setSkipStock(false);
    }
  }, [isAdhocSelected]);

  // Load stock balance on client select
  useEffect(() => {
    if (!clientId) {
      setStockOptions([]);
      setSelectedStock(null);
      return;
    }
    setStockLoading(true);
    setSelectedStock(null);
    setError('');
    api.getStockBalance(clientId)
      .then(data => {
        const opts = (data || []).filter(b => b.balance_sheets > 0);
        setStockOptions(opts);
        if (opts.length === 0 && !skipStock) {
          setError('No stock available for this client.');
        }
      })
      .catch(e => {
        console.error('Failed to load stock balance:', e);
        setStockOptions([]);
        if (!skipStock) {
          setError('Failed to load stock balance.');
        }
      })
      .finally(() => setStockLoading(false));
  }, [clientId, skipStock]);

  async function handleCreateAdhoc(e) {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const created = await doCreateAdhocClient(adhocForm);
      setClientId(created.id);
      setShowAdhocForm(false);
      setSkipStock(true); // default to skip stock for walk-ins
    } catch (e) {
      setError(e.message || 'Failed to create client');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSubmit() {
    if (!clientId || !quantity || Number(quantity) <= 0) return;
    if (!skipStock && !selectedStock) return;
    if (skipStock && (!customSize || !customGsm)) {
      setError('Size and GSM are required when skipping stock check.');
      return;
    }
    
    setSubmitting(true);
    setError('');
    try {
      await onSubmit({
        client_id: clientId,
        board_size: skipStock ? customSize : selectedStock.board_size,
        gsm: skipStock ? Number(customGsm) : selectedStock.gsm,
        quantity_sheets: Number(quantity),
        ticket_number: ticketNumber || undefined,
        job_details: {
          job_type: jobType || undefined,
          colors: colors ? Number(colors) : undefined,
          no_stock_tracking: skipStock ? true : undefined,
        },
      });
      onClose();
    } catch (e) {
      setError(e.message || 'Failed to create work card');
    } finally {
      setSubmitting(false);
    }
  }

  const qtyExceedsStock = selectedStock && Number(quantity) > selectedStock.balance_sheets;

  return createPortal(
    <div
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center',
        justifyContent: 'center', zIndex: 10000, animation: 'fadeIn 0.2s ease'
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'var(--bg-card)', border: '1px solid var(--border-color)',
          borderRadius: 'var(--radius-lg)', padding: 24, minWidth: 420, maxWidth: 520,
          maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 24px 60px rgba(0,0,0,0.5)'
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ margin: 0, fontSize: 16, color: 'var(--text-primary)' }}>
            <i className="fa-solid fa-plus-circle" style={{ color: 'var(--accent-cyan)', marginRight: 8 }}></i>
            Create Manual Work Card
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 18 }}>
            <i className="fa-solid fa-xmark"></i>
          </button>
        </div>

        {/* Step 1: Client Picker */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 4 }}>
            <label style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>Client *</label>
            <button 
              type="button" 
              onClick={() => setShowAdhocForm(!showAdhocForm)}
              style={{ background: 'none', border: 'none', color: 'var(--accent-cyan)', fontSize: 11, cursor: 'pointer', fontWeight: 600 }}
            >
              <i className={showAdhocForm ? "fa-solid fa-xmark" : "fa-solid fa-plus"}></i> 
              {showAdhocForm ? ' Cancel new walk-in client' : ' Add one-off walk-in client'}
            </button>
          </div>
          
          {showAdhocForm ? (
            <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', padding: 12 }}>
              <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 11, color: 'var(--text-muted)' }}>Name *</label>
                  <input type="text" required value={adhocForm.name} onChange={e => setAdhocForm(f => ({...f, name: e.target.value}))} className="editable-input" placeholder="e.g. Walk-in Customer" />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 11, color: 'var(--text-muted)' }}>Phone</label>
                  <input type="text" value={adhocForm.phone} onChange={e => setAdhocForm(f => ({...f, phone: e.target.value}))} className="editable-input" placeholder="Optional" />
                </div>
              </div>
              <Button size="sm" variant="primary" onClick={handleCreateAdhoc} disabled={!adhocForm.name || submitting}>
                {submitting ? 'Saving...' : 'Save Walk-in Client'}
              </Button>
            </div>
          ) : (
            <select
              value={clientId}
              onChange={e => {
                const newId = e.target.value;
                setClientId(newId);
                const isAdhoc = clients.find(c => c.id === newId)?.is_adhoc === true;
                setSkipStock(isAdhoc);
              }}
              style={{
                width: '100%', padding: '10px 12px', fontSize: 13,
                background: 'var(--bg-dark)', color: 'var(--text-primary)',
                border: '1px solid var(--border-color)', borderRadius: 'var(--radius-sm)'
              }}
            >
              <option value="">— Select client —</option>
              {clients.map(c => (
                <option key={c.id} value={c.id}>{c.name} {c.gstin ? `(${c.gstin})` : ''} {c.is_adhoc ? '(Walk-in)' : ''}</option>
              ))}
            </select>
          )}
        </div>

        {isAdhocSelected && !showAdhocForm && (
          <div style={{ marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: 'rgba(255, 145, 0, 0.1)', border: '1px solid rgba(255, 145, 0, 0.3)', borderRadius: 'var(--radius-sm)' }}>
            <input 
              type="checkbox" 
              id="skipStockCheck"
              checked={skipStock}
              onChange={e => setSkipStock(e.target.checked)}
              style={{ accentColor: 'var(--accent-orange)', width: 16, height: 16 }}
            />
            <label htmlFor="skipStockCheck" style={{ fontSize: 12, color: 'var(--text-primary)', cursor: 'pointer' }}>
              Skip stock tracking for this walk-in job
            </label>
          </div>
        )}

        {/* Step 2: Stock Material Picker */}
        {clientId && !showAdhocForm && (
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4, display: 'block', fontWeight: 600 }}>
              {skipStock ? 'Material Specifications *' : 'Stock Material *'}
            </label>
            {skipStock ? (
              <div style={{ display: 'flex', gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <input type="text" required placeholder="Size (e.g. 20x30)" value={customSize} onChange={e => setCustomSize(e.target.value)} className="editable-input" />
                </div>
                <div style={{ flex: 1 }}>
                  <input type="number" required placeholder="GSM (e.g. 250)" value={customGsm} onChange={e => setCustomGsm(e.target.value)} className="editable-input" />
                </div>
              </div>
            ) : stockLoading ? (
              <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>Loading stock balance…</p>
            ) : stockOptions.length === 0 ? (
              <p style={{ fontSize: 12, color: 'var(--accent-red)' }}>No available stock for this client.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {stockOptions.map((s, i) => {
                  const isSelected = selectedStock && selectedStock.board_size === s.board_size && selectedStock.gsm === s.gsm;
                  return (
                    <div
                      key={i}
                      onClick={() => setSelectedStock(s)}
                      style={{
                        padding: '10px 14px', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
                        border: `1px solid ${isSelected ? 'var(--accent-cyan)' : 'var(--border-color)'}`,
                        background: isSelected ? 'rgba(0, 242, 254, 0.06)' : 'var(--bg-dark)',
                        transition: 'all 0.15s ease'
                      }}
                    >
                      <div style={{ fontSize: 13, fontWeight: 600 }}>
                        {s.board_size} · {s.gsm} GSM
                        {s.material && <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}> · {s.material}</span>}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--accent-green)', marginTop: 2 }}>
                        Available: {s.balance_sheets.toLocaleString()} sheets
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Step 3: Quantity */}
        {(selectedStock || skipStock) && !showAdhocForm && (
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4, display: 'block', fontWeight: 600 }}>
              Quantity (sheets) *
            </label>
            <input
              type="number"
              min="1"
              max={skipStock ? undefined : selectedStock?.balance_sheets}
              value={quantity}
              onChange={e => setQuantity(e.target.value)}
              placeholder={skipStock ? "Qty" : `Max: ${selectedStock?.balance_sheets.toLocaleString()}`}
              style={{
                width: '100%', padding: '10px 12px', fontSize: 14,
                background: 'var(--bg-dark)', color: 'var(--text-primary)',
                border: '1px solid var(--border-color)', borderRadius: 'var(--radius-sm)',
                fontFamily: 'var(--font-mono)'
              }}
            />
            {qtyExceedsStock && !skipStock && (
              <div style={{ fontSize: 11, color: 'var(--accent-orange)', marginTop: 4 }}>
                ⚠️ Exceeds available stock ({selectedStock.balance_sheets.toLocaleString()} sheets). Server may reject.
              </div>
            )}
          </div>
        )}

        {/* Step 4: Optional fields */}
        {(selectedStock || skipStock) && !showAdhocForm && (
          <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4, display: 'block', fontWeight: 600 }}>Ticket Number</label>
              <input
                type="text"
                value={ticketNumber}
                onChange={e => setTicketNumber(e.target.value)}
                placeholder="e.g. MAN-001"
                style={{
                  width: '100%', padding: '10px 12px', fontSize: 13,
                  background: 'var(--bg-dark)', color: 'var(--text-primary)',
                  border: '1px solid var(--border-color)', borderRadius: 'var(--radius-sm)'
                }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4, display: 'block', fontWeight: 600 }}>Job Type</label>
              <input
                type="text"
                value={jobType}
                onChange={e => setJobType(e.target.value)}
                placeholder="e.g. Box Printing"
                style={{
                  width: '100%', padding: '10px 12px', fontSize: 13,
                  background: 'var(--bg-dark)', color: 'var(--text-primary)',
                  border: '1px solid var(--border-color)', borderRadius: 'var(--radius-sm)'
                }}
              />
            </div>
          </div>
        )}

        {(selectedStock || skipStock) && !showAdhocForm && (
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4, display: 'block', fontWeight: 600 }}>Colors (Offset)</label>
            <input
              type="number"
              min="0"
              value={colors}
              onChange={e => setColors(e.target.value)}
              placeholder="e.g. 4"
              style={{
                width: 120, padding: '10px 12px', fontSize: 13,
                background: 'var(--bg-dark)', color: 'var(--text-primary)',
                border: '1px solid var(--border-color)', borderRadius: 'var(--radius-sm)'
              }}
            />
          </div>
        )}

        {error && (
          <div style={{ fontSize: 12, color: 'var(--accent-red)', padding: '8px 0', marginBottom: 8 }}>
            ⚠️ {error}
          </div>
        )}

        {!showAdhocForm && (
          <div style={{ display: 'flex', gap: 10 }}>
            <Button variant="outline" onClick={onClose} style={{ flex: 1 }}>Cancel</Button>
            <Button
              variant="primary"
              disabled={submitting || !clientId || (!skipStock && !selectedStock) || !quantity || Number(quantity) <= 0}
              onClick={handleSubmit}
              style={{ flex: 1 }}
            >
              <i className="fa-solid fa-plus"></i> {submitting ? 'Creating…' : 'Create Work Card'}
            </Button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

// ── Main WorkCards Component ───────────────────────────────────
export default function WorkCards() {
  const { state, doCompleteWorkcard, doApproveWorkcard, doRejectWorkcard, doLinkClient, doCreateWorkcard, doDeleteWorkcard, cl, fetchWorkcards } = useApp();
  
  const [completing, setCompleting] = useState(null);
  const [actioning, setActioning]   = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy]             = useState('date_desc');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [deletingId, setDeletingId]     = useState(null);

  // Linking state
  const [linkingId, setLinkingId]       = useState(null);
  const [linkClientId, setLinkClientId] = useState('');


  const isFirstLoad = state.loading.workcards && !state.workcards.length;

  // Filter logic
  const filtered = useMemo(() => {
    let list = state.workcards;
    if (statusFilter === 'pending') {
      list = list.filter(w => w.status === 'pending');
    } else if (statusFilter === 'unlinked') {
      list = list.filter(w => w.status === 'pending' && !w.client_id);
    } else if (statusFilter === 'in_progress') {
      list = list.filter(w => w.status === 'in_progress');
    } else if (statusFilter === 'completed') {
      list = list.filter(w => w.status === 'completed');
    }
    return list;
  }, [state.workcards, statusFilter]);

  // Sort logic
  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      if (sortBy === 'date_desc') {
        return (b.date || b.recv_at || '').localeCompare(a.date || a.recv_at || '');
      }
      if (sortBy === 'date_asc') {
        return (a.date || a.recv_at || '').localeCompare(b.date || b.recv_at || '');
      }
      if (sortBy === 'logged_desc') {
        return (b.recv_at || '').localeCompare(a.recv_at || '');
      }
      if (sortBy === 'logged_asc') {
        return (a.recv_at || '').localeCompare(b.recv_at || '');
      }
      if (sortBy === 'status_asc') {
        return (a.status || '').localeCompare(b.status || '');
      }
      if (sortBy === 'status_desc') {
        return (b.status || '').localeCompare(a.status || '');
      }
      if (sortBy === 'no_asc') {
        return (a.no || '').localeCompare(b.no || '');
      }
      if (sortBy === 'no_desc') {
        return (b.no || '').localeCompare(a.no || '');
      }
      if (sortBy === 'qty_desc') {
        return (b.qty || 0) - (a.qty || 0);
      }
      if (sortBy === 'qty_asc') {
        return (a.qty || 0) - (b.qty || 0);
      }
      if (sortBy === 'client_asc') {
        const nameA = cl(a.client_id)?.name || '';
        const nameB = cl(b.client_id)?.name || '';
        return nameA.localeCompare(nameB);
      }
      return 0;
    });
  }, [filtered, sortBy, cl]);

  const cardsList = useMemo(() => {
    return sorted.filter(w => w.status === 'pending' || w.status === 'in_progress');
  }, [sorted]);

  const tableList = useMemo(() => {
    return sorted.filter(w => w.status === 'completed' || w.status === 'billed');
  }, [sorted]);


  // Actions
  async function handleApprove(id) {
    setActioning(id);
    try { await doApproveWorkcard(id); }
    catch (_) {} finally { setActioning(null); }
  }

  async function handleReject(id) {
    setActioning(id);
    try { await doRejectWorkcard(id); }
    catch (_) {} finally { setActioning(null); }
  }

  async function handleComplete(id) {
    setCompleting(id);
    try { await doCompleteWorkcard(id); }
    catch (_) {} finally { setCompleting(null); }
  }

  async function handleLinkClientSubmit(wcId) {
    if (!linkClientId) return;
    setActioning(wcId);
    try {
      await doLinkClient(wcId, linkClientId);
      setLinkingId(null);
      setLinkClientId('');
    } catch (_) {}
    finally { setActioning(null); }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this work card? This cannot be undone.')) return;
    setDeletingId(id);
    try {
      await doDeleteWorkcard(id);
    } catch (_) {}
    finally { setDeletingId(null); }
  }

  // Helper: check if card is deletable
  function isDeletable(card) {
    return (card.status === 'pending' || card.status === 'rejected') && !card.deducted;
  }

  // Print helper
  function handlePrint(card) {
    const client = state.clients.find(c => c.id === card.client_id);
    const isManual = isManualCard(card);
    const material = card.job?.paper_type || 'RS DUPLEX BOARDS';
    const balance = state.stockBalance?.find(b =>
      b.board_size === card.size && Number(b.gsm) === Number(card.gsm)
    );
    const balSheets = balance?.balance_sheets ?? 0;
    
    const signedUrl = _urlCache.get(card.id);
    const imgHtml = signedUrl
      ? `<div style="margin-top: 30px;"><p class="label">Ticket Scan</p><img src="${signedUrl}" style="max-width: 100%; max-height: 500px; border-radius: 8px; border: 1px solid #ccc;" /></div>`
      : isManual ? '<div style="margin-top: 30px;"><p class="label">Ticket Scan</p><div style="padding: 20px; border: 1px dashed #ccc; color: #666; font-size: 12px; display: inline-block;">N/A (Manual Job)</div></div>' : '';

    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    document.body.appendChild(iframe);

    const printDoc = iframe.contentWindow.document;
    printDoc.open();
    printDoc.write(`
      <html>
        <head>
          <title>Work Ticket #${card.no}</title>
          <style>
            body { font-family: system-ui, -apple-system, sans-serif; padding: 40px; color: #000; }
            .header { display: flex; justify-content: space-between; border-bottom: 2px solid #000; padding-bottom: 20px; margin-bottom: 30px; }
            h1 { margin: 0; font-size: 24px; }
            .badge { display: inline-block; padding: 4px 8px; border: 1px solid #000; font-size: 12px; font-weight: bold; text-transform: uppercase; }
            .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 30px; }
            .field { margin-bottom: 15px; }
            .label { font-size: 11px; color: #666; text-transform: uppercase; margin-bottom: 4px; letter-spacing: 0.5px; }
            .value { font-size: 16px; font-weight: 600; }
            @media print { 
              body { padding: 0; }
              @page { margin: 1.5cm; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div>
              <h1>Work Ticket #${card.no}</h1>
              <div style="margin-top: 8px;">Logged At: ${fmtD(card.recv_at)}</div>
            </div>
            <div style="text-align: right;">
              <span class="badge">${isManual ? 'MANUAL JOB' : 'BOT SCANNED'}</span>
              <div style="margin-top: 8px; font-size: 12px; font-weight: bold; text-transform: uppercase;">STATUS: ${card.status.replace('_', ' ')}</div>
            </div>
          </div>
          
          <div class="grid">
            <div class="field">
              <div class="label">Client</div>
              <div class="value">${client ? client.name : 'Unknown Client'}</div>
            </div>
            <div class="field">
              <div class="label">Job Type</div>
              <div class="value">${card.job?.job_type || 'Cards'}</div>
            </div>
            <div class="field">
              <div class="label">Paper Size</div>
              <div class="value">${card.size}</div>
            </div>
            <div class="field">
              <div class="label">GSM</div>
              <div class="value">${card.gsm}</div>
            </div>
            <div class="field">
              <div class="label">Material</div>
              <div class="value">${material}</div>
            </div>
            <div class="field">
              <div class="label">Print Quantity</div>
              <div class="value">${card.qty?.toLocaleString()} Sheets</div>
            </div>
            <div class="field">
              <div class="label">Colors (Offset)</div>
              <div class="value">${card.job?.colors || 0}</div>
            </div>
            <div class="field">
              <div class="label">Last Move</div>
              <div class="value">${card._raw?.updated_at ? fmtD(card._raw.updated_at) : '—'}</div>
            </div>
          </div>
          
          <div style="background: #f8f9fa; padding: 15px; border-radius: 8px; border: 1px solid #e2e8f0; display: inline-block;">
             <span class="label">Current Stock Balance:</span> <strong style="font-size: 14px; margin-left: 8px;">${balSheets.toLocaleString()} sheets</strong>
          </div>
          
          ${imgHtml}
          
          <div style="margin-top: 50px; padding-top: 20px; border-top: 1px solid #ccc; font-size: 11px; color: #666; text-align: center;">
            Aishwarya Printers - Internal Work Order
          </div>
          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
              }, 300);
            };
          </script>
        </body>
      </html>
    `);
    printDoc.close();

    // Clean up iframe after printing is presumed done
    setTimeout(() => {
      if (document.body.contains(iframe)) {
        document.body.removeChild(iframe);
      }
    }, 15000);
  }

  // Helper: check if card is manual
  function isManualCard(card) {
    return card._raw?.job_details?.source === 'manual';
  }

  // Count helper for filters
  const pendingCount = state.workcards.filter(w => w.status === 'pending').length;
  const unlinkedCount = state.workcards.filter(w => w.status === 'pending' && !w.client_id).length;

  if (isFirstLoad) {
    return <Layout><PageLoader label="Loading work tickets…" /></Layout>;
  }

  return (
    <Layout
      actions={
        <>
          <Button
            variant="primary"
            size="sm"
            onClick={() => setShowCreateModal(true)}
          >
            <i className="fa-solid fa-plus"></i> Create Work Card
          </Button>
          <Button
            variant="outline"
            className={statusFilter === 'all' ? 'active' : ''}
            onClick={() => setStatusFilter('all')}
          >
            All
          </Button>
          <Button
            variant="outline"
            className={statusFilter === 'pending' ? 'active' : ''}
            onClick={() => setStatusFilter('pending')}
          >
            Pending <span className="sub-badge">{pendingCount}</span>
          </Button>
          <Button
            variant="outline"
            className={statusFilter === 'unlinked' ? 'active' : ''}
            onClick={() => setStatusFilter('unlinked')}
          >
            Unlinked <span className="sub-badge" style={{ background: 'rgba(255, 23, 68, 0.15)', color: 'var(--accent-red)' }}>{unlinkedCount}</span>
          </Button>
          <Button
            variant="outline"
            className={statusFilter === 'in_progress' ? 'active' : ''}
            onClick={() => setStatusFilter('in_progress')}
          >
            In Progress
          </Button>
          <Button
            variant="outline"
            className={statusFilter === 'completed' ? 'active' : ''}
            onClick={() => setStatusFilter('completed')}
          >
            Completed
          </Button>
          <select
            value={sortBy}
            onChange={e => setSortBy(e.target.value)}
            className="select-dropdown"
            style={{
              background: 'var(--bg-dark)', 
              color: 'var(--text-primary)', 
              border: '1px solid var(--border-color)',
              borderRadius: 'var(--radius-md)', 
              padding: '6px 12px', 
              fontSize: 13, 
              outline: 'none',
              cursor: 'pointer',
              marginLeft: '8px'
            }}
          >
            <option value="date_desc">Sort: Ticket Date (Newest)</option>
            <option value="date_asc">Sort: Ticket Date (Oldest)</option>
            <option value="logged_desc">Sort: Date Logged (Newest)</option>
            <option value="logged_asc">Sort: Date Logged (Oldest)</option>
            <option value="status_asc">Sort: Status (A-Z)</option>
            <option value="status_desc">Sort: Status (Z-A)</option>
            <option value="no_asc">Sort: Ticket Number (A-Z)</option>
            <option value="no_desc">Sort: Ticket Number (Z-A)</option>
            <option value="qty_desc">Sort: Quantity (High-Low)</option>
            <option value="qty_asc">Sort: Quantity (Low-High)</option>
            <option value="client_asc">Sort: Client Name (A-Z)</option>
          </select>
          <Button variant="ghost" size="sm" onClick={fetchWorkcards}>↺</Button>
        </>
      }
    >
      <div className="workcards-scroll-wrapper" style={{ flexGrow: 1, overflowY: 'auto', paddingRight: '4px', display: 'flex', flexDirection: 'column', gap: '32px', height: '100%', minHeight: 0 }}>
        {/* Active Cards Grid (Pending / In Progress) */}
        {cardsList.length > 0 && (
          <div>
            {statusFilter === 'all' && (
              <h3 style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                <i className="fa-solid fa-clock-rotate-left"></i> Active Work Tickets
              </h3>
            )}
            <div className="workcards-grid">
              {cardsList.map(card => {
                const client = cl(card.client_id);
                const isUnlinked = !card.client_id;
                const material = card.job.paper_type || 'RS DUPLEX BOARDS';
                const balance = state.stockBalance?.find(b =>
                  b.board_size === card.size && Number(b.gsm) === Number(card.gsm)
                );
                const balSheets = balance?.balance_sheets ?? 0;
                const sufficient = Boolean(card.job?.no_stock_tracking) || balSheets >= card.qty;
                const isApproveEnabled = sufficient && card.client_id;
                const isManual = isManualCard(card);

                return (
                  <div 
                    key={card.id} 
                    className="workcard-item"
                    style={{ display: 'flex', flexDirection: 'column', transition: 'transform 0.2s, box-shadow 0.2s' }}
                  >
                    <div className="workcard-header-bar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px 8px 20px', borderBottom: '1px solid var(--border-color)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span className="workcard-ticket-no" style={{ position: 'static', fontSize: '16px', fontWeight: 700, color: 'var(--accent-cyan)', textShadow: 'none' }}>#{card.no}</span>
                        {isManual && (
                          <span style={{
                            fontSize: 9, padding: '2px 6px', borderRadius: 4,
                            background: 'rgba(156, 39, 176, 0.15)', color: '#CE93D8',
                            fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px'
                          }}>
                            Manual
                          </span>
                        )}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span className={`workcard-status-badge wc-status-${card.status.replace('_', '')}`} style={{ position: 'static' }}>
                          {card.status.replace('_', ' ')}
                        </span>
                        
                        <button
                          onClick={(e) => { e.stopPropagation(); handlePrint(card); }}
                          title="Print Work Ticket"
                          style={{
                            background: 'none', border: 'none', color: 'var(--text-muted)',
                            cursor: 'pointer', fontSize: 13, padding: '2px 4px',
                            transition: 'color 0.2s'
                          }}
                          onMouseEnter={e => e.currentTarget.style.color = 'var(--text-primary)'}
                          onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
                        >
                          <i className="fa-solid fa-print"></i>
                        </button>
                        
                        {isDeletable(card) && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDelete(card.id); }}
                            disabled={deletingId === card.id}
                            title="Delete work card"
                            style={{
                              background: 'none', border: 'none', color: 'var(--text-muted)',
                              cursor: 'pointer', fontSize: 13, padding: '2px 4px',
                              opacity: deletingId === card.id ? 0.4 : 0.5,
                              transition: 'opacity 0.2s, color 0.2s'
                            }}
                            onMouseEnter={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.color = 'var(--accent-red)'; }}
                            onMouseLeave={e => { e.currentTarget.style.opacity = '0.5'; e.currentTarget.style.color = 'var(--text-muted)'; }}
                          >
                            <i className="fa-solid fa-trash-can"></i>
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="workcard-body">
                      {!isUnlinked ? (
                        <div className="workcard-client-row">🏢 Client: {client?.name}</div>
                      ) : (
                        <div className="workcard-warning-banner">
                          <i className="fa-solid fa-exclamation-triangle"></i>
                          <span>Party GSTIN missing / unmatched</span>
                        </div>
                      )}

                      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', marginTop: 12 }}>
                        <div className="workcard-details-grid" style={{ flexGrow: 1, margin: 0 }}>
                          <div className="wc-grid-item">Job Type:<span>{card.job.job_type || 'Cards'}</span></div>
                          <div className="wc-grid-item">Paper Spec:<span>{card.size} / {card.gsm} GSM</span></div>
                          <div className="wc-grid-item">Print Qty:<span>{card.qty?.toLocaleString()} Sheets</span></div>
                          <div className="wc-grid-item">Colors count:<span>{card.job.colors || 0} (Offset)</span></div>
                          <div className="wc-grid-item" style={{ gridColumn: 'span 2' }}>Logged At:<span style={{ fontFamily: 'var(--font-mono)' }}>{fmtD(card.recv_at)}</span></div>
                          <div className="wc-grid-item" style={{ gridColumn: 'span 2' }}>Last Move:<span style={{ fontFamily: 'var(--font-mono)' }}>{card._raw?.updated_at ? fmtD(card._raw.updated_at) : '—'}</span></div>
                        </div>
                        <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                          <span style={{ fontSize: 9, color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 600 }}>Ticket Scan</span>
                          {card.raw_image_url === null && isManual ? (
                            <div style={{
                              width: 44, height: 44, borderRadius: 'var(--radius-sm)',
                              border: '1px dashed var(--border-color)', display: 'flex',
                              alignItems: 'center', justifyContent: 'center', fontSize: 11,
                              color: 'var(--text-muted)', backgroundColor: '#0f172a'
                            }}>
                              N/A
                            </div>
                          ) : (
                            <WorkcardImage id={card.id} ticketNo={card.no} />
                          )}
                        </div>
                      </div>

                      {card.status === 'pending' && card.client_id && (
                        <div className="workcard-stock-status">
                          <p>Material: <strong>{material}</strong></p>
                          {card.job?.no_stock_tracking ? (
                            <p>Stock Balance: <strong style={{ color: 'var(--accent-orange)' }}>Skipped (Walk-in)</strong></p>
                          ) : (
                            <p>Stock Balance: <strong style={{ color: sufficient ? 'var(--accent-green)' : 'var(--accent-red)' }}>{balSheets.toLocaleString()} sheets</strong> (Req: {card.qty?.toLocaleString()})</p>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="workcard-actions" onClick={e => e.stopPropagation()}>
                      {card.status === 'pending' ? (
                        isUnlinked ? (
                          linkingId === card.id ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, width: '100%' }}>
                              <select
                                value={linkClientId}
                                onChange={e => setLinkClientId(e.target.value)}
                                style={{
                                  background: 'var(--bg-dark)', color: 'var(--text-primary)', border: '1px solid var(--border-color)',
                                  borderRadius: 'var(--radius-sm)', padding: 6, fontSize: 12
                                }}
                              >
                                <option value="">— Select client —</option>
                                {state.clients.filter(c => c.status !== 'inactive').map(c => (
                                  <option key={c.id} value={c.id}>{c.name}</option>
                                ))}
                              </select>
                              <div style={{ display: 'flex', gap: 6 }}>
                                <Button variant="outline" size="sm" onClick={() => setLinkingId(null)} style={{ flex: 1 }}>Cancel</Button>
                                <Button variant="primary" size="sm" disabled={!linkClientId || actioning === card.id} onClick={() => handleLinkClientSubmit(card.id)} style={{ flex: 1 }}>Link</Button>
                              </div>
                            </div>
                          ) : (
                            <button className="btn btn-outline" style={{ width: '100%' }} onClick={() => { setLinkingId(card.id); setLinkClientId(''); }}>
                              <i className="fa-solid fa-link"></i> Link Client
                            </button>
                          )
                        ) : (
                          <>
                            <button className="btn btn-danger" disabled={actioning === card.id} onClick={() => handleReject(card.id)}>
                              <i className="fa-solid fa-xmark"></i> Reject
                            </button>
                            <button className="btn btn-success" disabled={!isApproveEnabled || actioning === card.id} onClick={() => handleApprove(card.id)}>
                              <i className="fa-solid fa-check"></i> Approve
                            </button>
                          </>
                        )
                      ) : (
                        <button className="btn btn-primary btn-block" disabled={completing === card.id} onClick={() => handleComplete(card.id)}>
                          <i className="fa-solid fa-circle-check"></i> {completing === card.id ? 'Completing…' : 'Complete Printing'}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Finalized Tabular List (Completed / Billed) */}
        {tableList.length > 0 && (
          <div>
            {statusFilter === 'all' && (
              <h3 style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 16, marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                <i className="fa-solid fa-box-archive"></i> Finalized Ticket History
              </h3>
            )}
            <div className="table-wrapper glass-card">
              <table className="dashboard-table">
                <thead>
                  <tr>
                    <th>Ticket Number</th>
                    <th>Movement Time</th>
                    <th>Date Logged</th>
                    <th>Ticket Scan</th>
                    <th>Client Name</th>
                    <th>Job Type</th>
                    <th>Paper Size</th>
                    <th>GSM</th>
                    <th>Colors</th>
                    <th>Quantity</th>
                    <th>Source</th>
                    <th className="align-right">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {tableList.map(card => {
                    const client = cl(card.client_id);
                    const isBilled = card.status === 'billed';
                    const statusBadge = isBilled ? 'badge-success' : 'badge-info';
                    const isManual = isManualCard(card);
                    
                    return (
                      <tr key={card.id}>
                        <td><strong style={{ fontFamily: 'var(--font-mono)' }}>#{card.no}</strong></td>
                        <td>{card._raw?.updated_at ? fmtD(card._raw.updated_at) : '—'}</td>
                        <td>{fmtD(card.recv_at)}</td>
                        <td>
                          {isManual && !card.raw_image_url ? (
                            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>—</span>
                          ) : (
                            <WorkcardImage id={card.id} ticketNo={card.no} />
                          )}
                        </td>
                        <td>{client ? client.name : 'Unknown Client'}</td>
                        <td>{card.job?.job_type || 'Job Card'}</td>
                        <td>{card.size}</td>
                        <td>{card.gsm} GSM</td>
                        <td>{card.job?.colors || 0}</td>
                        <td><strong>{card.qty?.toLocaleString()} sheets</strong></td>
                        <td>
                          {isManual ? (
                            <span style={{
                              fontSize: 9, padding: '2px 6px', borderRadius: 4,
                              background: 'rgba(156, 39, 176, 0.15)', color: '#CE93D8',
                              fontWeight: 700, textTransform: 'uppercase'
                            }}>Manual</span>
                          ) : (
                            <span style={{
                              fontSize: 9, padding: '2px 6px', borderRadius: 4,
                              background: 'rgba(0, 242, 254, 0.1)', color: 'var(--accent-cyan)',
                              fontWeight: 700, textTransform: 'uppercase'
                            }}>Bot</span>
                          )}
                        </td>
                        <td className="align-right">
                          <span className={`badge ${statusBadge}`} style={{ textTransform: 'uppercase', fontSize: 10 }}>
                            {card.status.replace('_', ' ')}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Empty State placeholder */}
        {cardsList.length === 0 && tableList.length === 0 && (
          <div className="no-selection-placeholder">
            <i className="fa-solid fa-clipboard-question"></i>
            <p>No work tickets found matching the filter.</p>
          </div>
        )}
      </div>

      {/* Manual Workcard Creation Modal */}
      {showCreateModal && (
        <CreateWorkcardModal
          clients={state.clients.filter(c => c.status !== 'inactive')}
          onClose={() => setShowCreateModal(false)}
          onSubmit={doCreateWorkcard}
        />
      )}
    </Layout>
  );

  
}
